import { Injectable, Injector } from '@angular/core';
import { AuthService } from 'auth';
import { Router } from '@angular/router';
import { HttpInterceptor, HttpRequest, HttpHandler } from '@angular/common/http';
import { catchError, switchMap, map, shareReplay, finalize } from 'rxjs/operators';
import { throwError, Observable } from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService?: AuthService;
  private refreshInProgress$: Observable<string> | null = null;

  constructor(
    private injector: Injector,
    private router: Router,
  ) {}

  // Coalesces concurrent 401s onto a single in-flight refresh call instead of
  // firing one refresh-token request per failed request (which races against
  // refresh-token rotation and can log the user out even after a successful refresh).
  private getRefreshedToken(): Observable<string> {
    if (!this.refreshInProgress$) {
      this.refreshInProgress$ = this.authService!.refreshToken().pipe(
        map((res: any) => {
          if (!res?.accessToken) {
            throw new Error('No access token in refresh response');
          }
          this.authService!.saveTokens(res.accessToken, res.refreshToken);
          return res.accessToken;
        }),
        shareReplay(1),
        finalize(() => {
          this.refreshInProgress$ = null;
        }),
      );
    }
    return this.refreshInProgress$;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    const accessToken = this.authService.getAccessToken();

    let authReq = req;
    const url = req.url.toLowerCase();
    // Skip auth header on public auth routes (login, refresh, etc.),
    // but always send Bearer token for authenticated auth routes (logout, reset-password/confirm, change-password).
    const authenticatedAuthRoutes = [
      '/authentication/logout',
      '/authentication/change-password/confirm',
      '/authentication/change-password',
    ];
    const isPublicAuthRequest =
      url.includes('/authentication/') &&
      !authenticatedAuthRoutes.some((route) => url.includes(route));

    if (accessToken && !isPublicAuthRequest) {
      authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    }

    return next.handle(authReq).pipe(
      catchError((err) => {
        if (err.status === 401) {
          return this.getRefreshedToken().pipe(
            switchMap((newAccessToken) => {
              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newAccessToken}`,
                },
              });

              return next.handle(retryReq);
            }),
            catchError(() => {
              this.authService!.clearTokens();
              this.router.navigate(['/login']);
              return throwError(() => err);
            }),
          );
        }

        return throwError(() => err);
      }),
    );
  }
}
